import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHistory } from '../lib/api';
import { formatDate, getPercentageColor, getPercentageBadge } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { History, ChevronRight, FileQuestion, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const HistoryPage = () => {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await getHistory();
      setAttempts(response.data);
    } catch (err) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fadeIn">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center flex-shrink-0">
              <History className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-zinc-900">Histórico</h1>
              <p className="text-zinc-500">Seus simulados realizados</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
          </div>
        ) : attempts.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileQuestion className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 mb-2">
              Nenhum simulado realizado
            </h3>
            <p className="text-zinc-500 mb-6">
              Comece agora mesmo a praticar!
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="start-first-quiz"
            >
              Iniciar Simulado
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {attempts.map((attempt) => {
              const hasRevisions = attempt.has_answer_key_revisions;
              const originalPercentage = attempt.original_percentage ?? attempt.percentage;
              const revisedPercentage = attempt.revised_percentage ?? attempt.percentage;
              const displayScore = attempt.revised_score ?? attempt.score;

              return (
                <div
                  key={attempt.id}
                  className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-6 hover:border-zinc-300 transition-all cursor-pointer card-hover"
                  onClick={() => navigate(`/result/${attempt.id}`)}
                  data-testid={`attempt-${attempt.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-zinc-900 mb-1 break-words">
                        {attempt.subject_name}
                      </h3>
                      <p className="text-sm text-zinc-500">
                        {formatDate(attempt.created_at)}
                      </p>
                      {hasRevisions && (
                        <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs font-medium text-amber-800">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Resultado atualizado após revisão de gabarito
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 w-full sm:w-auto">
                      <div className="text-left sm:text-right">
                        <p className={`text-2xl font-bold ${getPercentageColor(revisedPercentage)}`}>
                          {revisedPercentage.toFixed(1)}%
                        </p>
                        <p className="text-sm text-zinc-500 whitespace-nowrap">
                          {displayScore}/{attempt.total_questions} acertos
                        </p>
                        {hasRevisions && (
                          <p className="text-xs text-zinc-400 whitespace-nowrap">
                            Original: {originalPercentage.toFixed(1)}%
                          </p>
                        )}
                      </div>
                      
                      <div className={`badge ${getPercentageBadge(revisedPercentage)} flex-shrink-0`}>
                        {revisedPercentage >= 70 ? 'Aprovado' : 'Reprovado'}
                      </div>
                      
                      <ChevronRight className="hidden sm:block w-5 h-5 text-zinc-400 flex-shrink-0" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HistoryPage;
